export default {
  title: {
    id: 'rm_mine-my-phone-story04_name',
    defaultMessage: 'China’s grip on gallium and why it matters',
  },
  excerpt: {
    id: 'rm_mine-my-phone-story04_excerpt',
    defaultMessage:
      'Gallium is one of the most strategically important raw materials on the planet. It is the fuel behind rising technologies such as AI, defense electronics, and clean energy. Here, Sam Geall explores its importance to China and the risks of a highly concentrated supply chain.',
  },
  region0Name: {
    id: 'rm_mine-my-phone-story04_region0_name',
    defaultMessage: 'Beijing',
  },
  region1Name: {
    id: 'rm_mine-my-phone-story04_region1_name',
    defaultMessage: 'Washington, DC',
  },
  s1P1: {
    id: 'rm_mine-my-phone-story04_s1_p1',
    defaultMessage:
      'Gallium is soft and silvery-white. With body heat alone, it will pool in the palm of your hand. But this element – which is traded in quantities so small that its volumes tend to be measured in kilograms – remains a crucial part of global geopolitics. It is widely sought after, sitting right at the intersection of military competition, the clean energy transition, and the race for AI supremacy.',
  },
  s1P2: {
    id: 'rm_mine-my-phone-story04_s1_p2',
    defaultMessage:
      'Unusually, it is not mined. Rather, it occurs in very small concentrations in other ores (such as bauxite, from which aluminum is obtained) and is mostly a byproduct of industrial aluminum refining. Regardless, gallium has become one of the most strategically important raw materials on the planet today. This is because it is used in the wide-bandgap semiconductor – these help create electric vehicles, data centers, solar inverters, and military radar systems. Basically, all the things we rely on daily.',
  },
  s1P3: {
    id: 'rm_mine-my-phone-story04_s1_p3',
    defaultMessage:
      'In smartphones, meanwhile, it is essential for powering wireless communications and fast battery chargers. Even if we don’t particularly care about access to faster charging EVs or improved AI models, when these supplies are restricted, the effects ripple throughout the global economy—chipmakers pay more or wait longer for raw materials, while manufacturers face delays and rising costs. That’s not even mentioning how consumers bear the consequences of rising prices.',
  },
  s1P4: {
    id: 'rm_mine-my-phone-story04_s1_p4',
    defaultMessage:
      'Today, one country controls nearly all of the supply and has the power to dictate production.',
  },
  s2Title: {
    id: 'rm_mine-my-phone-story04_s2_title',
    defaultMessage: 'China’s leverage',
  },
  s2P1: {
    id: 'rm_mine-my-phone-story04_s2_p1',
    defaultMessage:
      'China [produces 99%](https://pubs.usgs.gov/periodicals/mcs2026/mcs2026-gallium.pdf) of the world’s gallium. The country’s grip on extraction of this once obscure element is now at a “chokepoint”: a geopolitical fault line (think the Strait of Hormuz, but functionally invisible) reshaping international relations between the world’s largest economies. Yet, China’s dominance over gallium wasn’t the product of long-term, let alone nefarious, planning.',
  },
  s2P2: {
    id: 'rm_mine-my-phone-story04_s2_p2',
    defaultMessage:
      'Decades of state-subsidized aluminum production in China – the country is by far the world’s largest exporter of the metal – put producers in the United States, Germany, and Japan out of business.',
  },
  s2P3: {
    id: 'rm_mine-my-phone-story04_s2_p3',
    defaultMessage:
      'This made China the only place where gallium extraction remained economically feasible. Aidan Powers-Riggs is an Associate Fellow at the Center for Strategic and International Studies (CSIS) think tank in Washington, DC, and an expert on critical raw materials. “I do not think this began as a geopolitical gambit,” he said. “Like many critical minerals, it happened by happenstance, and later [Chinese] authorities realized it could be useful leverage.”',
  },
  s3P1: {
    id: 'rm_mine-my-phone-story04_s3_p1',
    defaultMessage:
      'Beijing gradually realized they were sitting on a powerful weapon. After the United States (under the Biden administration in 2022) imposed restrictions on semiconductor exports (in a set of measures that squarely aimed to maintain US technological dominance in AI), China tested that weapon and introduced its first export controls on gallium in 2023.',
  },
  s3P2: {
    id: 'rm_mine-my-phone-story04_s3_p2',
    defaultMessage:
      'At the time, these were framed as standard “dual-use” agreements, where a company needs to obtain an export license from the Chinese economic and trade ministry, known as MOFCOM, to prove the material will not be used for military purposes. These were [widely](https://asia.nikkei.com/business/tech/semiconductors/china-limits-exports-of-chipmaking-metals-in-trade-spat-with-u.s) [understood](https://www.csis.org/analysis/understanding-chinas-gallium-sanctions) to be a retaliatory measure to the Biden sanctions the previous year.',
  },
  s3P3: {
    id: 'rm_mine-my-phone-story04_s3_p3',
    defaultMessage:
      'Still, foreign companies complied and supply levels returned to normal. But then in December 2024, and in response to a new round of US semiconductor export controls, “China moved from a dual-use licensing requirement to a named embargo on exports to the United States. That applied not just to gallium metal, but also gallium arsenide and gallium nitride.” These are the compounds of gallium needed, in particular, for advanced technologies.',
  },
  s3P4: {
    id: 'rm_mine-my-phone-story04_s3_p4',
    defaultMessage:
      '“A few months later, shortages began to appear and enforcement improved,” said Powers-Riggs. “It is one thing to write a rule; it is another to enforce one in a market where annual volumes are measured in kilograms.” In other words, by enforcing the sanctions, Beijing had exercised its control over gallium supply, and global markets began to notice.',
  },
  s3P5: {
    id: 'rm_mine-my-phone-story04_s3_p5',
    defaultMessage:
      'This tit-for-tat continued through to the next administration, when China responded to the [Trump administration trade tariffs](https://www.nytimes.com/2026/02/02/business/trump-tariffs-one-year-later.html) in 2025 by further escalating its restrictions across a whole range of so-called rare earths – from samarium, to scandium and yttrium. Now, companies – from automakers in Germany and France, to electronics firms in Japan and aerospace giants in the United States – were staring down the barrel of deepening, potentially crippling supply shortages.',
  },
  s4Title: {
    id: 'rm_mine-my-phone-story04_s4_title',
    defaultMessage: 'Stalemate',
  },
  s4P1: {
    id: 'rm_mine-my-phone-story04_s4_p1',
    defaultMessage:
      'When President Trump and his Chinese counterpart met in October 2025 on the sidelines of the Asia-Pacific Economic Cooperation (APEC) summit in Korea, critical minerals were no longer obscure. It was headline news that they agreed to suspend the escalation, and a White House fact sheet said that “China will issue general licenses valid for exports of rare earths, gallium, germanium, antimony, and graphite for the benefit of US end users and their suppliers around the world.”',
  },
  s4P2: {
    id: 'rm_mine-my-phone-story04_s4_p2',
    defaultMessage:
      'The topic was mentioned again after Trump’s visit to China in April 2026. But for gallium at least, claimed Powers-Riggs, “nothing has changed.” Trade in scarce commodities is an opaque and sensitive business, but it seems no licenses have been granted, and surging [prices](https://strategicmetalsinvest.com/gallium-prices/) are testament to an ongoing supply crunch. Gallium recently set another all-time high; its price is up by more than 30% since the start of 2026.',
  },
  s4P3: {
    id: 'rm_mine-my-phone-story04_s4_p3',
    defaultMessage:
      'Meanwhile, China added [a new export ban](https://www.cnbc.com/2026/06/29/china-japan-export-controls-watch-list-defense-entities.html) in January 2026 – this time on Japan, after its new Prime Minister Sanae Takaichi stated that an attack on Taiwan, home to the world’s most advanced chip manufacturers, could constitute an “existential threat” to Japan.',
  },
  s5Title: {
    id: 'rm_mine-my-phone-story04_s5_title',
    defaultMessage: 'Next steps',
  },
  s5P1: {
    id: 'rm_mine-my-phone-story04_s5_p1',
    defaultMessage:
      'So, what can other countries do about it? “There has actually been quite a lot of action over the past year or so,” said Powers-Riggs. “The simple way to put it is that wherever aluminum production is happening outside China, especially in an allied country, you should also be investing in gallium tailings extraction.”',
  },
  s5P2: {
    id: 'rm_mine-my-phone-story04_s5_p2',
    defaultMessage:
      'The Alcoa Wagerup Gallium plant in Western Australia, for example, under a joint investment and offtake agreement between the Japanese, American, and Australian governments, aims to produce 100 tonnes of gallium each year, or [10% of global demand](https://www.reuters.com/business/energy/alcoa-greenlights-australia-gallium-plant-with-us-japan-domestic-backing-2026-07-14/), according to the Australian trade minister. Other projects, in various stages of development, are based in Canada, the United States, Germany, and Jamaica, and some hope new processing technologies will result in more cost-effective extraction methods.',
  },
  s5P3: {
    id: 'rm_mine-my-phone-story04_s5_p3',
    defaultMessage:
      'Production will be hard to guarantee without coordinated agreements, especially given the difficult economics around such a scarce resource. “If China opens the tap,” Powers-Riggs noted, “and begins dumping material back onto the market, the price could crash and hurt these new industrial projects.”',
  },
  s5P4: {
    id: 'rm_mine-my-phone-story04_s5_p4',
    defaultMessage:
      'Recycling also helps, and Japan is currently a world leader here. Stockpiling is attractive (the United States has one such initiative named Project Vault), but limited as a solution. In the end, a combination of measures will be needed – and new international agreements like [FORGE](https://www.atlanticcouncil.org/dispatches/us-critical-minerals-policy-goes-collaborative-with-forge/) are being created to set price floors and preserve what countries increasingly see as strategic resources.',
  },
  s5P5: {
    id: 'rm_mine-my-phone-story04_s5_p5',
    defaultMessage:
      'In June 2026, [G7 leaders declined to name China](https://www.reuters.com/world/europe/g7-sets-up-critical-minerals-alliance-crisis-platform-2026-06-17/), but rather conspicuously agreed to “significantly reduce” their dependence on a “single supplier outside the G7” for rare earths and permanent magnets to below 60% by 2030, with the goal of reaching 50% “as soon as possible.” “We will work together with partners,” said the declaration, “to reduce critical dependencies and ensure that attempts or threats to weaponize economic dependencies fail.”',
  },
  s5P6: {
    id: 'rm_mine-my-phone-story04_s5_p6',
    defaultMessage:
      'Gallium is a microcosm of a bigger story about control over the raw materials that will power electric vehicles, train AI systems, and guide missile defense systems. These technologies of the future will hinge on who controls the materials and the supply chains that make them possible.',
  },
  s5P7: {
    id: 'rm_mine-my-phone-story04_s5_p7',
    defaultMessage:
      'Aluminum refineries – whether in eastern China or Western Australia – are some of the unlikely new battlefields in that competition.',
  },
  f1P1: {
    id: 'rm_mine-my-phone-story04_f1_p1',
    defaultMessage: 'A piece of gallium. Credit: Bloomberg / Getty',
  },
  f2P1: {
    id: 'rm_mine-my-phone-story04_f2_p1',
    defaultMessage:
      'U.S. President Donald Trump greets Chinese President Xi Jinping on October 30, 2025 in Busan, South Korea. Trump is meeting Xi for the first time since taking office for his second term, following months of growing tension between both countries. Credits: Andrew Harnik / Getty)',
  },
  q1: {
    id: 'rm_mine-my-phone-story04_q1',
    defaultMessage:
      '“I do not think this began as a geopolitical gambit. Like many critical minerals, it happened by happenstance, and later [Chinese] authorities realized it could be useful leverage.”',
  },
  q1Attr: {
    id: 'rm_mine-my-phone-story04_q1_attr',
    defaultMessage:
      '**Aidan Powers-Riggs**, Associate Fellow at the Center for Strategic and International Studies',
  },
  q2: {
    id: 'rm_mine-my-phone-story04_q2',
    defaultMessage:
      '“If China opens the tap and begins dumping material back onto the market, the price could crash.”',
  },
  q2Attr: {
    id: 'rm_mine-my-phone-story04_q2_attr',
    defaultMessage: '**Aidan Powers-Riggs**',
  },
} as const
